import { Button } from "@repo/ui/components/ui/button"
import { cn } from "@repo/ui/lib/utils"
import { Table } from "@tanstack/react-table"
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react"
import { useTranslations } from "use-intl"

interface DataTablePaginationProps<TData>
  extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  table: Table<TData>
  handlePageChangeAction?: (page: number) => void
}

function DataTablePagination<TData>({
  table,
  className,
  handlePageChangeAction,
}: DataTablePaginationProps<TData>) {
  const t = useTranslations("ui.dataTable.pagination")
  const handlePageChange = (page: number) => {
    if (handlePageChangeAction) handlePageChangeAction(page)
    table.setPageIndex(page)
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 px-2",
        className,
      )}
    >
      <div className="flex-1 text-sm text-muted-foreground">
        {table.options.enableRowSelection &&
          t("rowsSelected", {
            selected: table.getFilteredSelectedRowModel().rows.length,
            total: table.getFilteredRowModel().rows.length,
          })}
      </div>
      <div className="flex items-center gap-3 sm:gap-6 lg:gap-8">
        <div className="flex min-w-[100px] shrink-0 items-center justify-center text-sm font-medium">
          {t("pageOf", {
            page: table.getState().pagination.pageIndex + 1,
            pageCount: table.getPageCount(),
          })}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => handlePageChange(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">{t("firstPage")}</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() =>
              handlePageChange(table.getState().pagination.pageIndex - 1)
            }
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">{t("previousPage")}</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() =>
              handlePageChange(table.getState().pagination.pageIndex + 1)
            }
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">{t("nextPage")}</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => handlePageChange(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">{t("lastPage")}</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DataTablePagination
